import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { usuariosAPI, rolesAPI, describirError } from '../services/api';
import PageHeader from '../components/PageHeader';
import '../styles/Usuarios.css';

export default function Usuarios() {
  const { hasPermission, user: currentUser } = useAuth();

  const [usuarios, setUsuarios] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [feedback, setFeedback] = useState(null);

  // Filtros
  const [search, setSearch] = useState('');
  const [selectedRol, setSelectedRol] = useState('');
  const [selectedEstado, setSelectedEstado] = useState('');

  // Modales
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetTargetUser, setResetTargetUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');

  // Form State
  const [formData, setFormData] = useState({
    nombre: '',
    apellidoPaterno: '',
    apellidoMaterno: '',
    email: '',
    username: '',
    rolId: '',
    password: '',
    activo: true
  });
  const [submitting, setSubmitting] = useState(false);

  const cargarDatos = async () => {
    try {
      setLoading(true);
      setError(null);
      const [resUsers, resRoles] = await Promise.all([
        usuariosAPI.listar(),
        rolesAPI.listar()
      ]);
      setUsuarios(resUsers.data?.data || []);
      setRoles(resRoles.data?.data || []);
    } catch (err) {
      const parsed = describirError(err);
      setError(parsed.mensaje || 'Error al cargar los usuarios.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarDatos();
  }, []);

  const handleOpenCreate = () => {
    setEditingUser(null);
    setFormData({
      nombre: '',
      apellidoPaterno: '',
      apellidoMaterno: '',
      email: '',
      username: '',
      rolId: roles[0]?.id || '',
      password: '',
      activo: true
    });
    setShowModal(true);
  };

  const handleOpenEdit = (user) => {
    setEditingUser(user);
    setFormData({
      nombre: user.nombre || '',
      apellidoPaterno: user.apellidoPaterno || '',
      apellidoMaterno: user.apellidoMaterno || '',
      email: user.email || '',
      username: user.username || '',
      rolId: user.rolId || '',
      activo: user.activo
    });
    setShowModal(true);
  };

  const handleOpenReset = (user) => {
    setResetTargetUser(user);
    setNewPassword('');
    setShowResetModal(true);
  };

  const handleSaveUser = async (e) => {
    e.preventDefault();
    if (!formData.nombre || !formData.email || !formData.rolId) {
      alert('Por favor complete los campos obligatorios: Nombre, Email y Rol.');
      return;
    }
    if (!editingUser && (!formData.password || formData.password.length < 8)) {
      alert('La contraseña inicial debe tener al menos 8 caracteres.');
      return;
    }

    try {
      setSubmitting(true);
      if (editingUser) {
        await usuariosAPI.actualizar(editingUser.id, {
          nombre: formData.nombre,
          apellidoPaterno: formData.apellidoPaterno,
          apellidoMaterno: formData.apellidoMaterno,
          email: formData.email,
          username: formData.username || null,
          rolId: formData.rolId,
          activo: formData.activo
        });
        setFeedback({ tipo: 'exito', mensaje: 'Usuario actualizado correctamente.' });
      } else {
        await usuariosAPI.crear({
          nombre: formData.nombre,
          apellidoPaterno: formData.apellidoPaterno,
          apellidoMaterno: formData.apellidoMaterno,
          email: formData.email,
          username: formData.username || null,
          rolId: formData.rolId,
          password: formData.password,
          activo: formData.activo
        });
        setFeedback({ tipo: 'exito', mensaje: 'Usuario creado exitosamente.' });
      }
      setShowModal(false);
      cargarDatos();
    } catch (err) {
      const parsed = describirError(err);
      alert(parsed.mensaje || 'Error al guardar el usuario.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleStatus = async (user) => {
    if (user.id === currentUser?.id && user.activo) {
      alert('No puede desactivar su propia cuenta de usuario.');
      return;
    }
    const accion = user.activo ? 'bloquear' : 'activar';
    if (!window.confirm(`¿Está seguro de que desea ${accion} al usuario ${user.nombre}?`)) {
      return;
    }

    try {
      await usuariosAPI.cambiarEstado(user.id, !user.activo);
      setFeedback({ tipo: 'exito', mensaje: `Usuario ${user.activo ? 'bloqueado' : 'activado'} correctamente.` });
      cargarDatos();
    } catch (err) {
      const parsed = describirError(err);
      alert(parsed.mensaje || 'Error al cambiar estado del usuario.');
    }
  };

  const handleResetPasswordSubmit = async (e) => {
    e.preventDefault();
    if (!newPassword || newPassword.length < 8) {
      alert('La nueva contraseña debe tener al menos 8 caracteres.');
      return;
    }

    try {
      setSubmitting(true);
      await usuariosAPI.resetPassword(resetTargetUser.id, { nuevaPassword: newPassword });
      setFeedback({ tipo: 'exito', mensaje: `Contraseña restablecida exitosamente para ${resetTargetUser.nombre}.` });
      setShowResetModal(false);
    } catch (err) {
      const parsed = describirError(err);
      alert(parsed.mensaje || 'Error al restablecer contraseña.');
    } finally {
      setSubmitting(false);
    }
  };

  // Filtrado reactivo
  const usuariosFiltrados = useMemo(() => {
    return usuarios.filter((u) => {
      const matchSearch =
        !search ||
        `${u.nombre} ${u.apellidoPaterno || ''} ${u.apellidoMaterno || ''}`.toLowerCase().includes(search.toLowerCase()) ||
        u.email.toLowerCase().includes(search.toLowerCase()) ||
        (u.username && u.username.toLowerCase().includes(search.toLowerCase()));

      const matchRol = !selectedRol || u.rol?.codigo === selectedRol;
      const matchEstado =
        selectedEstado === '' || (selectedEstado === 'ACTIVO' ? u.activo : !u.activo);

      return matchSearch && matchRol && matchEstado;
    });
  }, [usuarios, search, selectedRol, selectedEstado]);

  const kpis = useMemo(() => {
    const total = usuarios.length;
    const activos = usuarios.filter((u) => u.activo).length;
    const inactivos = total - activos;
    const totalRoles = roles.length;
    return { total, activos, inactivos, totalRoles };
  }, [usuarios, roles]);

  const getRolBadgeClass = (codigo) => {
    switch (codigo) {
      case 'ADMIN': return 'badge-rol badge-rol-admin';
      case 'GERENTE': return 'badge-rol badge-rol-gerente';
      case 'COBRANZA': return 'badge-rol badge-rol-cobranza';
      case 'CAPTURISTA': return 'badge-rol badge-rol-capturista';
      case 'TESORERIA': return 'badge-rol badge-rol-tesoreria';
      case 'DIRECCION': return 'badge-rol badge-rol-direccion';
      default: return 'badge-rol badge-rol-consulta';
    }
  };

  return (
    <div className="usuarios-container">
      <PageHeader
        title="Usuarios del Sistema"
        subtitle="Administración de cuentas, roles y control de acceso (RBAC)"
        actions={
          hasPermission('USUARIOS_CREAR') && (
            <button
              id="btn-nuevo-usuario"
              className="btn btn-primary"
              onClick={handleOpenCreate}
            >
              + Nuevo Usuario
            </button>
          )
        }
      />

      {feedback && (
        <div style={{
          padding: '12px 18px',
          marginBottom: '16px',
          borderRadius: '8px',
          backgroundColor: feedback.tipo === 'exito' ? '#ecfdf5' : '#fef2f2',
          color: feedback.tipo === 'exito' ? '#065f46' : '#991b1b',
          border: `1px solid ${feedback.tipo === 'exito' ? '#a7f3d0' : '#fecaca'}`
        }}>
          {feedback.mensaje}
        </div>
      )}

      {error && (
        <div style={{
          padding: '12px 18px',
          marginBottom: '16px',
          borderRadius: '8px',
          backgroundColor: '#fef2f2',
          color: '#991b1b',
          border: '1px solid #fecaca'
        }}>
          {error}
        </div>
      )}

      {/* KPIs */}
      <div className="usuarios-kpis">
        <div className="usuarios-kpi-card">
          <span className="usuarios-kpi-title">Total Usuarios</span>
          <span className="usuarios-kpi-value">{kpis.total}</span>
        </div>
        <div className="usuarios-kpi-card">
          <span className="usuarios-kpi-title">Usuarios Activos</span>
          <span className="usuarios-kpi-value" style={{ color: '#059669' }}>{kpis.activos}</span>
        </div>
        <div className="usuarios-kpi-card">
          <span className="usuarios-kpi-title">Inactivos / Bloqueados</span>
          <span className="usuarios-kpi-value" style={{ color: kpis.inactivos > 0 ? '#dc2626' : '#64748b' }}>
            {kpis.inactivos}
          </span>
        </div>
        <div className="usuarios-kpi-card">
          <span className="usuarios-kpi-title">Roles Configurados</span>
          <span className="usuarios-kpi-value" style={{ color: '#2563eb' }}>{kpis.totalRoles}</span>
        </div>
      </div>

      {/* Barra de Filtros */}
      <div className="usuarios-filters-bar">
        <input
          type="text"
          className="usuarios-search-input"
          placeholder="Buscar por nombre, correo o usuario..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <select
          className="usuarios-select-filter"
          value={selectedRol}
          onChange={(e) => setSelectedRol(e.target.value)}
        >
          <option value="">Todos los Roles</option>
          {roles.map((r) => (
            <option key={r.id} value={r.codigo}>{r.nombre}</option>
          ))}
        </select>

        <select
          className="usuarios-select-filter"
          value={selectedEstado}
          onChange={(e) => setSelectedEstado(e.target.value)}
        >
          <option value="">Todos los Estados</option>
          <option value="ACTIVO">Activos</option>
          <option value="INACTIVO">Inactivos</option>
        </select>
      </div>

      {/* Tabla de Usuarios */}
      <div className="usuarios-table-card">
        <table className="usuarios-table">
          <thead>
            <tr>
              <th>Nombre Completo</th>
              <th>Email / Usuario</th>
              <th>Rol Asignado</th>
              <th>Estado</th>
              <th>Último Acceso</th>
              <th style={{ textAlign: 'right' }}>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="6" style={{ textAlign: 'center', padding: '32px', color: '#64748b' }}>
                  Cargando catálogo de usuarios...
                </td>
              </tr>
            ) : usuariosFiltrados.length === 0 ? (
              <tr>
                <td colSpan="6" style={{ textAlign: 'center', padding: '32px', color: '#64748b' }}>
                  No se encontraron usuarios que coincidan con los criterios.
                </td>
              </tr>
            ) : (
              usuariosFiltrados.map((u) => (
                <tr key={u.id}>
                  <td>
                    <div style={{ fontWeight: 600, color: '#0f172a' }}>
                      {u.nombre} {u.apellidoPaterno} {u.apellidoMaterno}
                    </div>
                  </td>
                  <td>
                    <div style={{ color: '#334155' }}>{u.email}</div>
                    {u.username && (
                      <div style={{ fontSize: '0.75rem', color: '#64748b' }}>@{u.username}</div>
                    )}
                  </td>
                  <td>
                    <span className={getRolBadgeClass(u.rol?.codigo)}>
                      {u.rol?.nombre || u.rol?.codigo}
                    </span>
                  </td>
                  <td>
                    {u.activo ? (
                      <span className="badge-estado-activo">
                        <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#059669' }} />
                        Activo
                      </span>
                    ) : (
                      <span className="badge-estado-inactivo">
                        <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#dc2626' }} />
                        Inactivo
                      </span>
                    )}
                  </td>
                  <td>
                    <div style={{ color: '#64748b', fontSize: '0.8125rem' }}>
                      {u.ultimoAccesoEn
                        ? new Date(u.ultimoAccesoEn).toLocaleString('es-MX', {
                            dateStyle: 'short',
                            timeStyle: 'short'
                          })
                        : 'Sin accesos'}
                    </div>
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <div className="usuarios-actions-cell" style={{ justifyContent: 'flex-end' }}>
                      {hasPermission('USUARIOS_EDITAR') && (
                        <button
                          className="usuarios-action-btn"
                          onClick={() => handleOpenEdit(u)}
                          title="Editar usuario"
                        >
                          Editar
                        </button>
                      )}

                      {hasPermission('USUARIOS_EDITAR') && (
                        <button
                          className="usuarios-action-btn"
                          onClick={() => handleOpenReset(u)}
                          title="Restablecer contraseña"
                        >
                          Reset Pass
                        </button>
                      )}

                      {hasPermission('USUARIOS_BLOQUEAR') && (
                        <button
                          className={u.activo ? 'usuarios-action-btn usuarios-action-btn-danger' : 'usuarios-action-btn usuarios-action-btn-success'}
                          onClick={() => handleToggleStatus(u)}
                          disabled={u.id === currentUser?.id}
                          title={u.activo ? 'Bloquear usuario' : 'Activar usuario'}
                        >
                          {u.activo ? 'Bloquear' : 'Activar'}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Modal Alta / Edición */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-card">
            <div className="modal-header">
              <h3 className="modal-title">{editingUser ? 'Editar Usuario' : 'Nuevo Usuario'}</h3>
              <button className="modal-close-btn" onClick={() => setShowModal(false)}>✕</button>
            </div>

            <form onSubmit={handleSaveUser}>
              <div className="modal-body">
                <div className="modal-form-grid">
                  <div className="login-field-group">
                    <label className="login-label">Nombre *</label>
                    <input
                      type="text"
                      className="usuarios-search-input"
                      value={formData.nombre}
                      onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                      required
                    />
                  </div>

                  <div className="login-field-group">
                    <label className="login-label">Apellido Paterno</label>
                    <input
                      type="text"
                      className="usuarios-search-input"
                      value={formData.apellidoPaterno}
                      onChange={(e) => setFormData({ ...formData, apellidoPaterno: e.target.value })}
                    />
                  </div>

                  <div className="login-field-group">
                    <label className="login-label">Apellido Materno</label>
                    <input
                      type="text"
                      className="usuarios-search-input"
                      value={formData.apellidoMaterno}
                      onChange={(e) => setFormData({ ...formData, apellidoMaterno: e.target.value })}
                    />
                  </div>

                  <div className="login-field-group">
                    <label className="login-label">Username (Opcional)</label>
                    <input
                      type="text"
                      className="usuarios-search-input"
                      placeholder="ej. jlopez"
                      value={formData.username}
                      onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    />
                  </div>

                  <div className="login-field-group form-field-full">
                    <label className="login-label">Correo Electrónico *</label>
                    <input
                      type="email"
                      className="usuarios-search-input"
                      placeholder="ej. usuario@midas.local"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                    />
                  </div>

                  <div className="login-field-group form-field-full">
                    <label className="login-label">Rol Asignado *</label>
                    <select
                      className="usuarios-select-filter"
                      style={{ width: '100%' }}
                      value={formData.rolId}
                      onChange={(e) => setFormData({ ...formData, rolId: e.target.value })}
                      required
                    >
                      {roles.map((r) => (
                        <option key={r.id} value={r.id}>
                          {r.nombre} ({r.codigo}) — {r.descripcion}
                        </option>
                      ))}
                    </select>
                  </div>

                  {!editingUser && (
                    <div className="login-field-group form-field-full">
                      <label className="login-label">Contraseña Inicial * (mínimo 8 caracteres)</label>
                      <input
                        type="password"
                        className="usuarios-search-input"
                        placeholder="••••••••"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        required
                        minLength={8}
                      />
                    </div>
                  )}

                  <div className="login-field-group form-field-full">
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={formData.activo}
                        onChange={(e) => setFormData({ ...formData, activo: e.target.checked })}
                      />
                      <span style={{ fontSize: '0.875rem', color: '#1e293b' }}>Usuario Activo en el Sistema</span>
                    </label>
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowModal(false)}
                  disabled={submitting}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={submitting}
                >
                  {submitting ? 'Guardando...' : editingUser ? 'Actualizar Usuario' : 'Crear Usuario'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Restablecer Contraseña */}
      {showResetModal && (
        <div className="modal-overlay">
          <div className="modal-card" style={{ maxWidth: '420px' }}>
            <div className="modal-header">
              <h3 className="modal-title">Restablecer Contraseña</h3>
              <button className="modal-close-btn" onClick={() => setShowResetModal(false)}>✕</button>
            </div>

            <form onSubmit={handleResetPasswordSubmit}>
              <div className="modal-body">
                <p style={{ fontSize: '0.875rem', color: '#64748b', margin: 0 }}>
                  Establecer una nueva contraseña de acceso para el usuario{' '}
                  <strong>{resetTargetUser?.nombre} ({resetTargetUser?.email})</strong>.
                </p>

                <div className="login-field-group">
                  <label className="login-label">Nueva Contraseña * (mínimo 8 caracteres)</label>
                  <input
                    type="password"
                    className="usuarios-search-input"
                    placeholder="••••••••"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={8}
                    autoFocus
                  />
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowResetModal(false)}
                  disabled={submitting}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={submitting}
                >
                  {submitting ? 'Guardando...' : 'Restablecer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
